import React from 'react';
import { Controller, FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import {
  CustomFormProps,
  DefaultFormActions,
  Fieldset,
  Form,
  useEnhancedForm,
} from '../../apiForm/form';
import SearchableFreeSoloMultiSelectField from '../../apiForm/form/input/SearchableFreeSoloMultiSelectField';
import SearchableFreeSoloSelectField from '../../apiForm/form/input/SearchableFreeSoloSelectField';

// add here predefined report types (but don't expose customer names)
const REPORT_TYPES: any[] = ['interim'];

const DEFAULT_VALUES: any = { backofficeReportingMode: 'unset' };
const REPORT_TYPE: any[] = ['cashier', 'device', 'unset'];
const MANAGER_TYPES: any[] = ['shopClosing'];

interface ReportingFormProps<
  TFieldValues extends FieldValues = FieldValues,
> extends CustomFormProps<TFieldValues> {}

export default function ReportingForm({
  defaultValues,
  errors = {},
  onSubmit,
}: ReportingFormProps<any>) {
  const { t } = useTranslation();
  const { handleSubmit, control, formState, getValues, reset } =
    useEnhancedForm({
      defaultValues: { ...DEFAULT_VALUES, ...defaultValues },
      errors,
    });

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <Fieldset
        category="reporting"
        legend={t('transactionConfig.reporting.headline')}
      >
        <SearchableFreeSoloMultiSelectField
          control={control}
          name="reportTypes"
          label={t('transactionConfig.reporting.input.reportTypes')}
          helperText={t('transactionConfig.reporting.helperText.reportTypes')}
          options={REPORT_TYPES}
        />
        <hr />
        <Controller
          name="backofficeReportingMode"
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <FormControl error={!!error} fullWidth>
              <FormLabel id="cashier-report-type-label">
                {t('transactionConfig.reporting.input.backoffice')}
              </FormLabel>
              <ToggleButtonGroup
                exclusive
                color="primary"
                value={value ?? null}
                onChange={(_, selectedType) => {
                  if (selectedType === 'unset') {
                    onChange(undefined);
                  }

                  if (selectedType !== null) {
                    onChange(selectedType);
                  }
                }}
              >
                {REPORT_TYPE.map((type) => (
                  <ToggleButton key={type} value={type}>
                    {type}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </FormControl>
          )}
        />
        <hr />
        <SearchableFreeSoloSelectField
          control={control}
          name="cashierReportTypes" // cashcount
          label={t('transactionConfig.reporting.input.reportType')}
          options={REPORT_TYPE}
          helperText={t(
            'transactionConfig.reporting.helperText.cashierReportTypes',
          )}
        />
        <SearchableFreeSoloMultiSelectField
          control={control}
          name="managerReportTypes" // closing
          label={t('transactionConfig.reporting.input.reportType')}
          options={MANAGER_TYPES}
          helperText={t(
            'transactionConfig.reporting.helperText.managerReportTypes',
          )}
        />
      </Fieldset>

      <DefaultFormActions
        formState={formState}
        noCancelNavigation
        reset={reset}
        getValues={getValues}
      />
    </Form>
  );
}
